import { Box, Stack, Typography } from '@mui/material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FC, useMemo } from 'react'

import { AddressSearchInput } from 'components/common/AddressSearchInput'
import { Country } from 'constants/country'
import { GRAY_900 } from 'constants/styling/theme'
import { InfoOutlined } from '@mui/icons-material'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUITooltip } from 'components/common/MUITooltip'
import { VAT_FORMAT_EXAMPLES } from 'utils/forms/validators'
import { useAuth0 } from 'utils/auth'
import { useInvoicingAddressForm } from './InvoicingAddressForm.context'
import { useTranslation } from 'react-i18next'

/**
 * Renders all input fields for the invoicing address form.
 *
 * @remarks
 * This component consumes the {@link useInvoicingAddressForm} context hook
 * to bind each {@link MUIInputField} to its corresponding form state,
 * error messages, and validation handlers.
 *
 * Layout is handled via Material-UI {@link Stack} components for responsive spacing.
 *
 * @component
 * @example
 * ```tsx
 * <InvoicingAddressFormFields />
 * ```
 */
export const InvoicingAddressFormFields: FC = () => {
  /** Provides i18n translations for field labels and placeholders (signup carries the address-search copy). */
  const { t } = useTranslation(['invoicing_address_form', 'country', 'signup'])

  /** Auth0 roles of currently logged user — admins can freely select the country. */
  const { roles } = useAuth0()

  /** Accesses form field bindings and current validation errors. */
  const { fields, errors, applyPlace } = useInvoicingAddressForm()

  /** Dynamic VAT placeholder based on current country */
  const vatPlaceholder = useMemo(() => {
    const format = VAT_FORMAT_EXAMPLES[fields.country.value]
    return format ? t('vat_number_placeholder', { format }) : ''
  }, [fields.country.value, t])


  return (
    <Stack gap={2} className="fs-exclude">

      {/* Address name */}
      <MUIInputField
        label={t('address_name')}
        required
        isError={!!errors.addressName}
        errorText={errors.addressName}
        {...fields.addressName}
      />

      {/* Company name */}
      <MUIInputField
        label={t('company_name')}
        required
        isError={!!errors.companyOrIndividualName}
        errorText={errors.companyOrIndividualName}
        {...fields.companyOrIndividualName}
      />

      {/* Street and apartment — the street input doubles as the address search: typing surfaces
        * Google predictions, picking one fills street/ZIP/city; typing without picking stays
        * plain manual entry (ISSUE-3082) */}
      <Stack gap={0.5}>
        <AddressSearchInput
          label={t('street_and_number')}
          placeholder={t('signup:address_search_placeholder')}
          required
          isError={!!errors.streetAndNumber}
          errorText={errors.streetAndNumber}
          value={fields.streetAndNumber.value}
          onChange={fields.streetAndNumber.onChange}
          restrictToCountry={fields.country.value || undefined}
          onSelect={applyPlace}
        />
        <MUIInputField
          isError={!!errors.apartmentNumber}
          errorText={errors.apartmentNumber}
          placeholder={t('apartment')}
          {...fields.apartmentNumber}
        />
      </Stack>

      {/* Region and country */}
      <Stack direction="row" gap={1}>

        <Box flex="1 1 50%">
          <MUIInputField
            label={t('region')}
            isError={!!errors.region}
            errorText={errors.region}
            {...fields.region}
          />
        </Box>

        <Box flex="1 1 50%">

          {roles.isAdmin &&
            <MUIDropdown
              button={(_, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t('country')}
                  type={DropdownInputTypes.SELECTOR}
                  isError={!!errors.country}
                  errorText={errors.country}
                  value={fields.country.value ? t(`country:${fields.country.value}`) : ''}
                  onClick={action}
                />
              )}
            >
              <Box maxHeight="30rem">
                {Object.values(Country).map(countryKey => (
                  <MUIDropdownItem
                    key={countryKey}
                    onClick={() => fields.country.onChange({ target: { value: countryKey } } as React.ChangeEvent<HTMLInputElement>)}
                  >
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                      {t(`country:${countryKey}`, countryKey)}
                    </Typography>
                  </MUIDropdownItem>
                ))}
              </Box>
            </MUIDropdown>
          }

          {!roles.isAdmin &&
            <MUIInputField
              label={t('country')}
              disabled
              required
              {...fields.country}
              // Converts actual value to translated one for viewing purposes only
              value={fields.country.value ? t(`country:${fields.country.value}`) : ''}
              endIcon={
                <MUITooltip content={t('country_tooltip')}>
                  <InfoOutlined sx={{ cursor: 'help' }} />
                </MUITooltip>
              }
            />
          }

        </Box>

      </Stack>

      {/* City and ZIP */}
      <Stack direction="row" gap={1}>

        <Box flex="1 1 50%">
          <MUIInputField
            label={t('city')}
            required
            isError={!!errors.city}
            errorText={errors.city}
            {...fields.city}
          />
        </Box>

        <Box flex="1 1 50%">
          <MUIInputField
            label={t('zip_code')}
            required
            isError={!!errors.postalCode}
            errorText={errors.postalCode}
            {...fields.postalCode}
          />
        </Box>

      </Stack>

      {/* VAT number */}
      <Box width="50%">
        <MUIInputField
          label={t('vat_number')}
          isError={!!errors.vatNumber}
          errorText={errors.vatNumber}
          placeholder={vatPlaceholder}
          endIcon={
            <MUITooltip content={t('vat_number_tooltip')}>
              <InfoOutlined sx={{ cursor: 'help' }} />
            </MUITooltip>
          }
          {...fields.vatNumber}
        />
      </Box>

    </Stack>
  )
}
